import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Plus, Edit2, Eye, ArrowUpDown, ArrowUp, ArrowDown, Search, Trash2, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateBR } from '../utils/dateFormat';
import { useContext } from 'react';
import { AuthContext } from '../App';

export default function Solicitacoes() {
  const { user } = useContext(AuthContext);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [atendentes, setAtendentes] = useState([]);
  const [tiposUso, setTiposUso] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'criado_em', direction: 'desc' });
  const [filtroGlobal, setFiltroGlobal] = useState('');
  const [buscaCliente, setBuscaCliente] = useState('');
  const [clientesDropdownOpen, setClientesDropdownOpen] = useState(false);
  const [valorClienteFormatado, setValorClienteFormatado] = useState('');
  const [valorFornecedorFormatado, setValorFornecedorFormatado] = useState('');
  const [editingStatus, setEditingStatus] = useState(null);
  const [editingPagamento, setEditingPagamento] = useState(null);
  const [menuAcoesAberto, setMenuAcoesAberto] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [novoTipoDialogOpen, setNovoTipoDialogOpen] = useState(false);
  const [novoTipoNome, setNovoTipoNome] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [currentSolicitacao, setCurrentSolicitacao] = useState(null);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [formData, setFormData] = useState({
    cliente_id: '',
    tipo_uso: '',
    cidade_destino: '',
    quantidade_adultos: 1,
    quantidade_criancas: 0,
    idades_criancas: '',
    check_in: '',
    check_out: '',
    valor_cotacao: 0,
    valor_fornecedor: 0,
    situacao_pagamento: 'Aguardando',
    status: 'Pendente',
    fornecedor_id: '',
    atendente_id: '',
    observacoes: '',
  });

  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clientesDropdownOpen && !event.target.closest('.cliente-dropdown-container')) {
        setClientesDropdownOpen(false);
      }
      if (editingStatus && !event.target.closest('.status-dropdown-container')) {
        setEditingStatus(null);
      }
      if (editingPagamento && !event.target.closest('.pagamento-dropdown-container')) {
        setEditingPagamento(null);
      }
      if (menuAcoesAberto && !event.target.closest('.menu-acoes-container')) {
        setMenuAcoesAberto(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [clientesDropdownOpen, editingStatus, editingPagamento, menuAcoesAberto]);

  const carregarDados = async () => {
    try {
      const [solRes, cliRes, fornRes, atendRes, tiposRes] = await Promise.all([
        axios.get(`${API}/solicitacoes`),
        axios.get(`${API}/clientes`),
        axios.get(`${API}/fornecedores`),
        axios.get(`${API}/usuarios`),
        axios.get(`${API}/tipos-uso`),
      ]);
      setSolicitacoes(solRes.data);
      setClientes(cliRes.data);
      setFornecedores(fornRes.data);
      setAtendentes(atendRes.data);
      setTiposUso(tiposRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Garantir que os valores sejam números válidos
      const dadosParaEnviar = {
        ...formData,
        valor_cotacao: parseFloat(formData.valor_cotacao) || 0,
        valor_fornecedor: parseFloat(formData.valor_fornecedor) || 0
      };
      
      if (editMode) {
        await axios.put(`${API}/solicitacoes/${currentSolicitacao.id}`, dadosParaEnviar);
        toast.success('Solicitação atualizada com sucesso!');
      } else {
        await axios.post(`${API}/solicitacoes`, dadosParaEnviar);
        toast.success('Solicitação criada com sucesso!');
      }
      setDialogOpen(false);
      resetForm();
      await carregarDados();
      // Disparar evento customizado para atualizar Dashboard e Relatórios
      window.dispatchEvent(new CustomEvent('solicitacaoModificada'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar solicitação');
    }
  };

  const resetForm = () => {
    setFormData({
      cliente_id: '',
      tipo_uso: '',
      cidade_destino: '',
      quantidade_adultos: 1,
      quantidade_criancas: 0,
      idades_criancas: '',
      check_in: '',
      check_out: '',
      valor_cotacao: 0,
      valor_fornecedor: 0,
      situacao_pagamento: 'Aguardando',
      status: 'Pendente',
      fornecedor_id: '',
      atendente_id: '',
      observacoes: '',
    });
    setEditMode(false);
    setCurrentSolicitacao(null);
    setClienteSelecionado(null);
    setBuscaCliente('');
    setClientesDropdownOpen(false);
    setValorClienteFormatado('');
    setValorFornecedorFormatado('');
  };

  const formatarMoeda = (valor) => {
    if (!valor) return '';
    // Remove tudo que não é dígito
    const apenasDigitos = valor.replace(/\D/g, '');
    if (!apenasDigitos) return '';
    // Converte para número e divide por 100 para ter centavos
    const numero = parseFloat(apenasDigitos) / 100;
    // Formata com 2 casas decimais
    return numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const desformatarMoeda = (valorFormatado) => {
    if (!valorFormatado) return 0;
    // Remove pontos e substitui vírgula por ponto
    const numero = valorFormatado.replace(/\./g, '').replace(',', '.');
    return parseFloat(numero) || 0;
  };

  const handleValorClienteChange = (e) => {
    const valor = e.target.value;
    const formatado = formatarMoeda(valor);
    setValorClienteFormatado(formatado);
    const numeroDesformatado = desformatarMoeda(formatado);
    setFormData(prevData => ({ ...prevData, valor_cotacao: numeroDesformatado }));
  };

  const handleValorFornecedorChange = (e) => {
    const valor = e.target.value;
    const formatado = formatarMoeda(valor);
    setValorFornecedorFormatado(formatado);
    const numeroDesformatado = desformatarMoeda(formatado);
    setFormData(prevData => ({ ...prevData, valor_fornecedor: numeroDesformatado }));
  };

  const handleClienteChange = (clienteId) => {
    setFormData(prevData => ({ ...prevData, cliente_id: clienteId }));
    const cliente = clientes.find(c => c.id === clienteId);
    setClienteSelecionado(cliente);
    setBuscaCliente(cliente ? `${cliente.nome} - ${cliente.telefone}` : '');
    setClientesDropdownOpen(false);
  };

  const clientesFiltrados = clientes.filter(c => {
    const termo = buscaCliente.toLowerCase();
    return (
      c.nome.toLowerCase().includes(termo) ||
      c.telefone.toLowerCase().includes(termo) ||
      (c.numero_associado && c.numero_associado.toLowerCase().includes(termo))
    );
  });

  const handleAdicionarTipoUso = async () => {
    if (!novoTipoNome.trim()) {
      toast.error('Digite o nome do tipo de uso');
      return;
    }

    try {
      await axios.post(`${API}/tipos-uso`, { nome: novoTipoNome });
      toast.success('Tipo de uso adicionado com sucesso!');
      setNovoTipoNome('');
      setNovoTipoDialogOpen(false);
      
      // Recarregar tipos
      const tiposRes = await axios.get(`${API}/tipos-uso`);
      setTiposUso(tiposRes.data);
      
      // Selecionar o novo tipo
      setFormData({ ...formData, tipo_uso: novoTipoNome });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar tipo de uso');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar esta solicitação?')) return;
    
    try {
      await axios.delete(`${API}/solicitacoes/${id}`);
      toast.success('Solicitação deletada com sucesso!');
      await carregarDados();
      // Disparar evento customizado para atualizar Dashboard e Relatórios
      window.dispatchEvent(new CustomEvent('solicitacaoModificada'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao deletar solicitação');
    }
  };

  const handleStatusChange = async (id, novoStatus) => {
    try {
      await axios.put(`${API}/solicitacoes/${id}`, { status: novoStatus });
      toast.success('Status atualizado!');
      setEditingStatus(null);
      // Atualizar localmente e recarregar
      await carregarDados();
      // Disparar evento customizado para atualizar Dashboard e Relatórios
      window.dispatchEvent(new CustomEvent('solicitacaoModificada'));
    } catch (error) {
      toast.error('Erro ao atualizar status');
      setEditingStatus(null);
    }
  };

  const handlePagamentoChange = async (id, novoPagamento) => {
    try {
      await axios.put(`${API}/solicitacoes/${id}`, { situacao_pagamento: novoPagamento });
      toast.success('Pagamento atualizado!');
      setEditingPagamento(null);
      // Atualizar localmente e recarregar
      await carregarDados();
      // Disparar evento customizado para atualizar Dashboard e Relatórios
      window.dispatchEvent(new CustomEvent('solicitacaoModificada'));
    } catch (error) {
      toast.error('Erro ao atualizar pagamento');
      setEditingPagamento(null);
    }
  };

  const statusOptions = ['Pendente', 'Em Tratamento', 'Montada', 'Enviada', 'Concluída', 'Cancelada'];
  const pagamentoOptions = ['Aguardando', 'Pago', 'Pendente'];

  const handleEdit = (solicitacao) => {
    setCurrentSolicitacao(solicitacao);
    setFormData({
      cliente_id: solicitacao.cliente_id,
      tipo_uso: solicitacao.tipo_uso,
      cidade_destino: solicitacao.cidade_destino || '',
      quantidade_adultos: solicitacao.quantidade_adultos || 1,
      quantidade_criancas: solicitacao.quantidade_criancas || 0,
      idades_criancas: solicitacao.idades_criancas || '',
      check_in: solicitacao.check_in,
      check_out: solicitacao.check_out,
      valor_cotacao: solicitacao.valor_cotacao,
      valor_fornecedor: solicitacao.valor_fornecedor || 0,
      situacao_pagamento: solicitacao.situacao_pagamento,
      status: solicitacao.status,
      fornecedor_id: solicitacao.fornecedor_id || '',
      atendente_id: solicitacao.atendente_id || '',
      observacoes: solicitacao.observacoes || '',
    });
    // Encontrar e definir o cliente selecionado
    const cliente = clientes.find(c => c.id === solicitacao.cliente_id);
    setClienteSelecionado(cliente);
    setBuscaCliente(cliente ? `${cliente.nome} - ${cliente.telefone}` : '');
    // Formatar valores para exibição
    setValorClienteFormatado(solicitacao.valor_cotacao.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    setValorFornecedorFormatado((solicitacao.valor_fornecedor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    setEditMode(true);
    setDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pendente':
        return 'bg-yellow-100 text-yellow-800';
      case 'Em Tratamento':
        return 'bg-blue-100 text-blue-800';
      case 'Montada':
        return 'bg-purple-100 text-purple-800';
      case 'Enviada':
        return 'bg-cyan-100 text-cyan-800';
      case 'Concluída':
        return 'bg-green-100 text-green-800';
      case 'Cancelada':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPagamentoColor = (situacao) => {
    switch (situacao) {
      case 'Aguardando':
        return 'bg-orange-100 text-orange-800';
      case 'Pago':
        return 'bg-green-100 text-green-800';
      case 'Pendente':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const calcularTempoAtendimento = (sol) => {
    if (!sol.data_hora_inicio_atendimento) return '-';
    
    const inicio = new Date(sol.data_hora_inicio_atendimento);
    const fim = sol.data_hora_conclusao ? new Date(sol.data_hora_conclusao) : new Date();
    
    const diffMs = fim - inicio;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${diffDays}d ${diffHours % 24}h`;
    } else if (diffHours > 0) {
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `${diffHours}h ${diffMinutes}m`;
    } else {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes}m`;
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedAndFilteredData = () => {
    let filtered = [...solicitacoes];

    // Aplicar filtro global
    if (filtroGlobal) {
      const termo = filtroGlobal.toLowerCase();
      filtered = filtered.filter(sol => {
        return (
          sol.cliente_nome?.toLowerCase().includes(termo) ||
          sol.tipo_uso?.toLowerCase().includes(termo) ||
          sol.cidade_destino?.toLowerCase().includes(termo) ||
          sol.status?.toLowerCase().includes(termo) ||
          sol.situacao_pagamento?.toLowerCase().includes(termo) ||
          sol.atendente_nome?.toLowerCase().includes(termo) ||
          new Date(sol.criado_em).toLocaleDateString('pt-BR').includes(termo) ||
          sol.check_in?.includes(termo) ||
          sol.check_out?.includes(termo) ||
          sol.valor_cotacao?.toString().includes(termo)
        );
      });
    }

    // Aplicar ordenação
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Tratamento especial para campos específicos
      if (sortConfig.key === 'criado_em' || sortConfig.key === 'check_in' || sortConfig.key === 'check_out') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (sortConfig.key === 'valor_cotacao') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else if (sortConfig.key === 'cliente_nome' || sortConfig.key === 'tipo_uso' || sortConfig.key === 'cidade_destino' || sortConfig.key === 'status' || sortConfig.key === 'situacao_pagamento' || sortConfig.key === 'atendente_nome') {
        aValue = (aValue || '').toLowerCase();
        bValue = (bValue || '').toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-3 w-3 ml-1 inline opacity-40" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1 inline" />
      : <ArrowDown className="h-3 w-3 ml-1 inline" />;
  };

  const solicitacoesFiltradas = getSortedAndFilteredData();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="solicitacoes-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 lg:mb-8 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-1 lg:mb-2">Solicitações</h1>
          <p className="text-sm sm:text-base text-slate-600">Gerencie as solicitações de reservas</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto" data-testid="nova-solicitacao-btn">
              <Plus className="mr-2 h-4 w-4 sm:h-5 sm:w-5" /> Nova Solicitação
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Editar Solicitação' : 'Nova Solicitação'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative cliente-dropdown-container">
                <Label>Cliente *</Label>
                <Input
                  value={buscaCliente}
                  onChange={(e) => {
                    setBuscaCliente(e.target.value);
                    setClientesDropdownOpen(true);
                    if (!e.target.value) {
                      setFormData({ ...formData, cliente_id: '' });
                      setClienteSelecionado(null);
                    }
                  }}
                  onClick={() => setClientesDropdownOpen(true)}
                  placeholder="Digite para buscar cliente..."
                  required
                  data-testid="input-busca-cliente"
                  autoComplete="off"
                />
                {clientesDropdownOpen && clientesFiltrados.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {clientesFiltrados.map((c) => (
                      <div
                        key={c.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleClienteChange(c.id);
                        }}
                        className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                      >
                        <div className="font-medium">{c.nome}</div>
                        <div className="text-xs text-slate-500">
                          {c.telefone}
                          {c.numero_associado && ` | Nº ${c.numero_associado}`}
                          {c.tipo_cliente && ` | ${c.tipo_cliente}`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {clienteSelecionado && (
                <div className="space-y-2 p-3 bg-slate-50 rounded-md">
                  <Label className="text-slate-500">Informações do Cliente</Label>
                  <div className="flex gap-2 items-center flex-wrap">
                    <Badge variant={clienteSelecionado.tipo_cliente === 'Associado' ? 'default' : 'secondary'}>
                      {clienteSelecionado.tipo_cliente}
                    </Badge>
                    <Badge className={clienteSelecionado.vinculo === 'Titular' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                      {clienteSelecionado.vinculo || 'Titular'}
                    </Badge>
                    {clienteSelecionado.numero_associado && (
                      <span className="text-sm text-slate-600">
                        Nº {clienteSelecionado.numero_associado}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Tipo de Uso *</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setNovoTipoDialogOpen(true)}
                      className="h-6 text-xs text-blue-600"
                      data-testid="btn-adicionar-tipo-uso"
                    >
                      + Adicionar Novo
                    </Button>
                  </div>
                  <Select
                    value={formData.tipo_uso}
                    onValueChange={(value) => setFormData({ ...formData, tipo_uso: value })}
                    required
                  >
                    <SelectTrigger data-testid="select-tipo-uso">
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {tiposUso.map((tipo) => (
                        <SelectItem key={tipo.id} value={tipo.nome}>
                          {tipo.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Cidade de Destino *</Label>
                  <Input
                    value={formData.cidade_destino}
                    onChange={(e) => setFormData({ ...formData, cidade_destino: e.target.value })}
                    placeholder="Ex: Natal, Rio de Janeiro"
                    required
                    data-testid="input-cidade-destino"
                  />
                </div>

                <div>
                  <Label>Quantidade de Adultos *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.quantidade_adultos}
                    onChange={(e) => setFormData({ ...formData, quantidade_adultos: parseInt(e.target.value) || 1 })}
                    required
                    data-testid="input-quantidade-adultos"
                  />
                </div>

                <div>
                  <Label>Quantidade de Crianças</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.quantidade_criancas}
                    onChange={(e) => setFormData({ ...formData, quantidade_criancas: parseInt(e.target.value) || 0 })}
                    data-testid="input-quantidade-criancas"
                  />
                </div>

                {formData.quantidade_criancas > 0 && (
                  <div className="col-span-2">
                    <Label>Idades das Crianças (separadas por vírgula)</Label>
                    <Input
                      value={formData.idades_criancas}
                      onChange={(e) => setFormData({ ...formData, idades_criancas: e.target.value })}
                      placeholder="Ex: 5, 8, 12"
                      data-testid="input-idades-criancas"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Digite as idades separadas por vírgula
                    </p>
                  </div>
                )}

                <div>
                  <Label>Check-in *</Label>
                  <Input
                    type="date"
                    value={formData.check_in}
                    onChange={(e) => setFormData({ ...formData, check_in: e.target.value })}
                    required
                    data-testid="input-check-in"
                  />
                </div>

                <div>
                  <Label>Check-out *</Label>
                  <Input
                    type="date"
                    value={formData.check_out}
                    onChange={(e) => setFormData({ ...formData, check_out: e.target.value })}
                    required
                    data-testid="input-check-out"
                  />
                </div>

                <div>
                  <Label>Valor Cliente (R$)</Label>
                  <Input
                    type="text"
                    value={valorClienteFormatado}
                    onChange={handleValorClienteChange}
                    placeholder="Digite o valor (ex: 150000 = R$ 1.500,00)"
                    data-testid="input-valor-cliente"
                  />
                </div>

                <div>
                  <Label>Valor Fornecedor (R$)</Label>
                  <Input
                    type="text"
                    value={valorFornecedorFormatado}
                    onChange={handleValorFornecedorChange}
                    placeholder="Digite o valor (ex: 100000 = R$ 1.000,00)"
                    data-testid="input-valor-fornecedor"
                  />
                </div>

                <div>
                  <Label>Situação do Pagamento</Label>
                  <Select
                    value={formData.situacao_pagamento}
                    onValueChange={(value) => setFormData({ ...formData, situacao_pagamento: value })}
                  >
                    <SelectTrigger data-testid="select-pagamento">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Aguardando">Aguardando</SelectItem>
                      <SelectItem value="Pago">Pago</SelectItem>
                      <SelectItem value="Pendente">Pendente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="select-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pendente">Pendente</SelectItem>
                      <SelectItem value="Em Tratamento">Em Tratamento</SelectItem>
                      <SelectItem value="Montada">Montada</SelectItem>
                      <SelectItem value="Enviada">Enviada</SelectItem>
                      <SelectItem value="Concluída">Concluída</SelectItem>
                      <SelectItem value="Cancelada">Cancelada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Fornecedor</Label>
                  <Select
                    value={formData.fornecedor_id}
                    onValueChange={(value) => setFormData({ ...formData, fornecedor_id: value })}
                  >
                    <SelectTrigger data-testid="select-fornecedor">
                      <SelectValue placeholder="Selecione o fornecedor" />
                    </SelectTrigger>
                    <SelectContent>
                      {fornecedores.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {user?.funcao === 'gerente' && (
                  <div>
                    <Label>Atendente</Label>
                    <Select
                      value={formData.atendente_id}
                      onValueChange={(value) => setFormData({ ...formData, atendente_id: value })}
                    >
                      <SelectTrigger data-testid="select-atendente">
                        <SelectValue placeholder="Selecione o atendente" />
                      </SelectTrigger>
                      <SelectContent>
                        {atendentes.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div>
                <Label>Observações</Label>
                <Textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  placeholder="Anotações e observações internas"
                  rows={3}
                  data-testid="textarea-observacoes"
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="salvar-solicitacao-btn">
                  {editMode ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Campo de busca */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Buscar..."
            value={filtroGlobal}
            onChange={(e) => setFiltroGlobal(e.target.value)}
            className="pl-10 text-sm"
            data-testid="input-filtro-global"
          />
        </div>
      </div>

      {/* Filtros rápidos de ordenação */}
      <div className="mb-4 flex flex-wrap gap-2">
        <span className="text-xs text-slate-600 self-center font-medium">Ordenar por:</span>
        <Badge
          onClick={() => handleSort('criado_em')}
          className={`cursor-pointer text-xs ${
            sortConfig.key === 'criado_em' ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
          }`}
        >
          Data {sortConfig.key === 'criado_em' && getSortIcon('criado_em')}
        </Badge>
        <Badge
          onClick={() => handleSort('cliente_nome')}
          className={`cursor-pointer text-xs ${
            sortConfig.key === 'cliente_nome' ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
          }`}
        >
          Cliente {sortConfig.key === 'cliente_nome' && getSortIcon('cliente_nome')}
        </Badge>
        <Badge
          onClick={() => handleSort('status')}
          className={`cursor-pointer text-xs ${
            sortConfig.key === 'status' ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
          }`}
        >
          Status {sortConfig.key === 'status' && getSortIcon('status')}
        </Badge>
        <Badge
          onClick={() => handleSort('check_in')}
          className={`cursor-pointer text-xs ${
            sortConfig.key === 'check_in' ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
          }`}
        >
          Check-in {sortConfig.key === 'check_in' && getSortIcon('check_in')}
        </Badge>
        <Badge
          onClick={() => handleSort('valor_cotacao')}
          className={`cursor-pointer text-xs ${
            sortConfig.key === 'valor_cotacao' ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
          }`}
        >
          Valor {sortConfig.key === 'valor_cotacao' && getSortIcon('valor_cotacao')}
        </Badge>
        <Badge
          onClick={() => handleSort('situacao_pagamento')}
          className={`cursor-pointer text-xs ${
            sortConfig.key === 'situacao_pagamento' ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
          }`}
        >
          Pagamento {sortConfig.key === 'situacao_pagamento' && getSortIcon('situacao_pagamento')}
        </Badge>
      </div>

      {/* Layout Mobile - Cards */}
      <div className="lg:hidden space-y-4">
        {solicitacoesFiltradas.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 text-center text-slate-500">
            {filtroGlobal ? 'Nenhuma solicitação encontrada' : 'Nenhuma solicitação cadastrada'}
          </div>
        ) : (
          solicitacoesFiltradas.map((sol) => (
            <div key={sol.id} className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              {/* Cabeçalho do Card */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800 text-sm">{sol.cliente_nome}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {new Date(sol.criado_em).toLocaleDateString('pt-BR')} às {new Date(sol.criado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <div className="relative status-dropdown-container">
                  <Badge 
                    className={`${getStatusColor(sol.status)} cursor-pointer hover:opacity-80 text-xs`}
                    onClick={() => setEditingStatus(editingStatus === sol.id ? null : sol.id)}
                  >
                    {sol.status}
                  </Badge>
                  {editingStatus === sol.id && (
                    <div className="absolute z-50 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg p-2 space-y-1 min-w-[120px]">
                      {statusOptions.map((status) => (
                        <Badge
                          key={status}
                          className={`${getStatusColor(status)} cursor-pointer hover:opacity-80 w-full block text-center text-xs`}
                          onClick={() => handleStatusChange(sol.id, status)}
                        >
                          {status}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Conteúdo do Card */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Tipo:</span>
                  <span className="font-medium text-slate-800">{sol.tipo_uso}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Destino:</span>
                  <span className="font-medium text-slate-800">{sol.cidade_destino || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Check-in:</span>
                  <span className="font-medium text-slate-800">{formatDateBR(sol.check_in)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Check-out:</span>
                  <span className="font-medium text-slate-800">{formatDateBR(sol.check_out)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Pessoas:</span>
                  <span className="font-medium text-slate-800">
                    {sol.quantidade_adultos || 1} adulto(s)
                    {sol.quantidade_criancas > 0 && `, ${sol.quantidade_criancas} criança(s)`}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Valores:</span>
                  <div className="text-right">
                    <div className="font-semibold text-slate-800">{sol.valor_cotacao.toFixed(2)}</div>
                    <div className="text-xs text-slate-500">{sol.valor_fornecedor ? sol.valor_fornecedor.toFixed(2) : '0.00'}</div>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Pagamento:</span>
                  <div className="relative pagamento-dropdown-container">
                    <Badge 
                      className={`${getPagamentoColor(sol.situacao_pagamento)} cursor-pointer hover:opacity-80 text-xs`}
                      onClick={() => setEditingPagamento(editingPagamento === sol.id ? null : sol.id)}
                    >
                      {sol.situacao_pagamento}
                    </Badge>
                    {editingPagamento === sol.id && (
                      <div className="absolute z-50 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg p-2 space-y-1 min-w-[100px]">
                        {pagamentoOptions.map((pagamento) => (
                          <Badge
                            key={pagamento}
                            className={`${getPagamentoColor(pagamento)} cursor-pointer hover:opacity-80 w-full block text-center text-xs`}
                            onClick={() => handlePagamentoChange(sol.id, pagamento)}
                          >
                            {pagamento}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {sol.atendente_nome && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Atendente:</span>
                    <span className="font-medium text-slate-800">{sol.atendente_nome}</span>
                  </div>
                )}
              </div>

              {/* Ações do Card */}
              <div className="flex gap-2 mt-4 pt-3 border-t border-slate-200">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(sol)}
                  className="flex-1"
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Editar
                </Button>
                {user?.funcao === 'gerente' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(sol.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Layout Desktop - Tabela */}
      <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-slate-200 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs xl:text-sm">Cliente</TableHead>
              <TableHead className="text-xs xl:text-sm">Destino</TableHead>
              <TableHead className="text-xs xl:text-sm">Período</TableHead>
              <TableHead className="text-xs xl:text-sm">Status</TableHead>
              <TableHead className="text-xs xl:text-sm">Valor</TableHead>
              <TableHead className="text-xs xl:text-sm">Pagamento</TableHead>
              <TableHead className="text-xs xl:text-sm w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {solicitacoesFiltradas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                  {filtroGlobal ? 'Nenhuma solicitação encontrada com os critérios de busca' : 'Nenhuma solicitação encontrada'}
                </TableCell>
              </TableRow>
            ) : (
              solicitacoesFiltradas.map((sol) => (
                <TableRow key={sol.id} data-testid={`solicitacao-row-${sol.id}`}>
                  {/* Cliente */}
                  <TableCell className="font-medium">
                    <div className="text-xs xl:text-sm">{sol.cliente_nome}</div>
                    <div className="text-xs text-slate-500 mt-1">{sol.tipo_uso}</div>
                    <div className="text-xs text-slate-500">
                      {sol.quantidade_adultos || 1} adulto(s)
                      {sol.quantidade_criancas > 0 && `, ${sol.quantidade_criancas} criança(s)`}
                    </div>
                  </TableCell>
                  
                  {/* Destino */}
                  <TableCell>
                    <div className="text-xs xl:text-sm">{sol.cidade_destino || '-'}</div>
                    {sol.atendente_nome && (
                      <div className="text-xs text-slate-500 mt-1">Atend: {sol.atendente_nome}</div>
                    )}
                  </TableCell>
                  
                  {/* Período */}
                  <TableCell>
                    <div className="text-xs xl:text-sm">
                      <div><span className="text-slate-600">In:</span> {formatDateBR(sol.check_in)}</div>
                      <div className="mt-1"><span className="text-slate-600">Out:</span> {formatDateBR(sol.check_out)}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        {new Date(sol.criado_em).toLocaleDateString('pt-BR')} {new Date(sol.criado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </TableCell>
                  
                  {/* Status */}
                  <TableCell>
                    <div className="relative status-dropdown-container">
                      <Badge 
                        className={`${getStatusColor(sol.status)} cursor-pointer hover:opacity-80 text-xs`}
                        onClick={() => setEditingStatus(editingStatus === sol.id ? null : sol.id)}
                      >
                        {sol.status}
                      </Badge>
                      {editingStatus === sol.id && (
                        <div className="absolute z-50 mt-1 bg-white border border-slate-200 rounded-md shadow-lg p-2 space-y-1 min-w-[120px]">
                          {statusOptions.map((status) => (
                            <Badge
                              key={status}
                              className={`${getStatusColor(status)} cursor-pointer hover:opacity-80 w-full block text-center`}
                              onClick={() => handleStatusChange(sol.id, status)}
                            >
                              {status}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{calcularTempoAtendimento(sol)}</div>
                  </TableCell>
                  
                  {/* Valor */}
                  <TableCell>
                    <div className="text-xs xl:text-sm">
                      <div className="font-medium">R$ {sol.valor_cotacao.toFixed(2)}</div>
                      <div className="text-xs text-slate-500 mt-1">Forn: {sol.valor_fornecedor ? sol.valor_fornecedor.toFixed(2) : '0.00'}</div>
                    </div>
                  </TableCell>
                  
                  {/* Pagamento */}
                  <TableCell>
                    <div className="relative pagamento-dropdown-container">
                      <Badge 
                        className={`${getPagamentoColor(sol.situacao_pagamento)} cursor-pointer hover:opacity-80 text-xs`}
                        onClick={() => setEditingPagamento(editingPagamento === sol.id ? null : sol.id)}
                      >
                        {sol.situacao_pagamento}
                      </Badge>
                      {editingPagamento === sol.id && (
                        <div className="absolute z-50 mt-1 bg-white border border-slate-200 rounded-md shadow-lg p-2 space-y-1 min-w-[100px]">
                          {pagamentoOptions.map((pagamento) => (
                            <Badge
                              key={pagamento}
                              className={`${getPagamentoColor(pagamento)} cursor-pointer hover:opacity-80 w-full block text-center`}
                              onClick={() => handlePagamentoChange(sol.id, pagamento)}
                            >
                              {pagamento}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  
                  {/* Ações */}
                  <TableCell>
                    <div className="relative menu-acoes-container">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setMenuAcoesAberto(menuAcoesAberto === sol.id ? null : sol.id)}
                        className="h-8 w-8 p-0"
                        data-testid={`menu-acoes-${sol.id}`}
                      >
                        <MoreVertical className="h-4 w-4 text-slate-600" />
                      </Button>
                      {menuAcoesAberto === sol.id && (
                        <div className="absolute right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg py-1 z-50 min-w-[140px]">
                          <button
                            onClick={() => {
                              handleEdit(sol);
                              setMenuAcoesAberto(null);
                            }}
                            className="w-full px-4 py-2 text-sm text-left hover:bg-slate-100 flex items-center gap-2"
                            data-testid={`edit-solicitacao-${sol.id}`}
                          >
                            <Edit2 className="h-4 w-4 text-slate-600" />
                            Editar
                          </button>
                          {user?.funcao === 'gerente' && (
                            <button
                              onClick={() => {
                                handleDelete(sol.id);
                                setMenuAcoesAberto(null);
                              }}
                              className="w-full px-4 py-2 text-sm text-left hover:bg-red-50 flex items-center gap-2 text-red-600"
                              data-testid={`delete-solicitacao-${sol.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                              Excluir
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialog para adicionar novo tipo de uso */}
      <Dialog open={novoTipoDialogOpen} onOpenChange={setNovoTipoDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Novo Tipo de Uso</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do Tipo *</Label>
              <Input
                value={novoTipoNome}
                onChange={(e) => setNovoTipoNome(e.target.value)}
                placeholder="Ex: Cruzeiro, Aéreo, Transfer"
                data-testid="input-novo-tipo"
                onKeyPress={(e) => e.key === 'Enter' && handleAdicionarTipoUso()}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setNovoTipoDialogOpen(false);
                  setNovoTipoNome('');
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAdicionarTipoUso}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="btn-salvar-tipo"
              >
                Adicionar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}